// src/router/index.js
import Vue from 'vue'
import VueRouter from 'vue-router'

Vue.use(VueRouter)

const LAUNCH_CLOSED = true

const PUBLIC_PATHS = new Set([
  '/login',
  '/register',
  '/waitlist-register',
  '/waitlist/complete',
  '/privacy',
  '/terms',
  '/blog',
  '/reset-password',
  '/__/auth/action',
])

const routes = [
  { path: '/', redirect: '/login' },

  // ── Firebase auth action interceptor ────────────────────────────────────────
  // Catches Firebase's default password reset links and redirects to our page
  {
    path: '/__/auth/action',
    meta: { public: true },
    beforeEnter(to, from, next) {
      const { mode, oobCode, apiKey, lang, continueUrl } = to.query
      if (mode === 'resetPassword') {
        next({
          path: '/reset-password',
          query: { mode, oobCode, apiKey, lang, continueUrl }
        })
      } else {
        // Other modes (email verification, etc.) - just proceed
        next()
      }
    },
    component: () => import('@/views/ResetPasswordView.vue'),
  },

  // ── Public / guest ──────────────────────────────────────────────────────────
  { path: '/login',    name: 'Login',    component: () => import('@/views/LoginView.vue'),    meta: { guestOnly: true } },
  { path: '/register', name: 'Register', component: () => import('@/views/RegisterView.vue'), meta: { guestOnly: true } },

  { path: '/privacy',        name: 'privacy',       component: () => import('@/views/PrivacyView.vue'),       meta: { public: true } },
  { path: '/terms',          name: 'terms',         component: () => import('@/views/TermsView.vue'),         meta: { public: true } },
  { path: '/support', name: 'Support', component: () => import('@/views/SupportView.vue'), meta: { public: true } },
  { path: '/reset-password', name: 'ResetPassword', component: () => import('@/views/ResetPasswordView.vue'), meta: { public: true } },
  { path: '/blog',           name: 'Blog',          component: () => import('@/views/BlogListView.vue') },
  { path: '/blog/:slug',     name: 'BlogPost',      component: () => import('@/views/BlogPostView.vue') },
  { path: '/about',          name: 'About',         component: () => import('@/views/AboutView.vue') },

  // ── Waitlist (public) ───────────────────────────────────────────────────────
  { path: '/waitlist-register', name: 'WaitlistRegister', component: () => import('@/views/WaitlistRegister.vue'), meta: { public: true } },
  { path: '/waitlist/complete',  name: 'WaitlistComplete', component: () => import('@/views/WaitlistComplete.vue'), meta: { public: true } },

  // ── Admin ───────────────────────────────────────────────────────────────────
  { path: '/admin',          name: 'AdminDashboard', component: () => import('@/views/AdminDashboard.vue'),        meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin-home',                             component: () => import('@/views/AdminHomeView.vue'),         meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin-search',   name: 'AdminSearch',    component: () => import('@/views/AdminSearchView.vue'),       meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin-settings', name: 'AdminSettings',  component: () => import('@/views/AdminSettingsView.vue'),     meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin/waitlist', name: 'AdminWaitlist',  component: () => import('@/views/AdminWaitlist.vue'),         meta: { requiresAuth: true, requiresAdmin: true } },
  { path: '/admin-messages', name: 'AdminMessages',  component: () => import('@/views/AdminConversationList.vue'), meta: { requiresAuth: true, requiresAdmin: true } },

  // ── Auth-required ───────────────────────────────────────────────────────────
  { path: '/home',      name: 'HomeView',          component: () => import('@/views/HomeView.vue'),              meta: { requiresAuth: true } },
  { path: '/giver',     name: 'GiverHome',         component: () => import('@/views/GiverHomeView.vue'),         meta: { requiresAuth: true, userType: 'giver'  } },
  { path: '/dashboard', name: 'CustomerDashboard', component: () => import('@/views/CustomerDashboardView.vue'), meta: { requiresAuth: true, userType: 'member' } },

  { path: '/profile',                name: 'Profile',                 component: () => import('@/views/Profile.vue'),                    meta: { requiresAuth: true } },
  { path: '/search',                 name: 'Search',                  component: () => import('@/views/SearchView.vue'),                 meta: { requiresAuth: true } },
  { path: '/messages',               name: 'Messages',                component: () => import('@/views/ConversationView.vue'),           meta: { requiresAuth: true } },
  { path: '/member-details',         name: 'MemberDetails',           component: () => import('@/views/UserDetailsView.vue'),            meta: { requiresAuth: true } },
  { path: '/member',                 name: 'MemberFriendsView',       component: () => import('@/views/MemberFriendsView.vue'),          meta: { requiresAuth: true }, props: true },
  { path: '/member-settings',        name: 'MemberSettings',          component: () => import('@/views/MemberSettingsView.vue'),         meta: { requiresAuth: true } },
  { path: '/giver-settings',         name: 'GiverSettings',           component: () => import('@/views/GiverSettingsView.vue'),          meta: { requiresAuth: true } },
  { path: '/security-settings',      name: 'SecuritySettingsView',    component: () => import('@/views/SecuritySettingsView.vue'),       meta: { requiresAuth: true }, props: true },
  { path: '/personal-information',   name: 'PersonalInformationView', component: () => import('@/views/PersonalInformationView.vue'),   meta: { requiresAuth: true } },
  { path: '/explore',                name: 'ExploreView',             component: () => import('@/views/ExploreView.vue'),                meta: { requiresAuth: true } },
  { path: '/notifications',          name: 'Notifications',           component: () => import('@/views/NotificationView.vue'),           meta: { requiresAuth: true } },
  { path: '/chat',                   name: 'ChatView',                component: () => import('@/views/ChatView.vue'),                   meta: { requiresAuth: true } },
  { path: '/groups',                 name: 'GroupView',               component: () => import('@/views/GroupView.vue'),                  meta: { requiresAuth: true } },
  { path: '/group-list',             name: 'GroupListView',           component: () => import('@/views/GroupView.vue'),                  meta: { requiresAuth: true } },
  { path: '/groups/create',          name: 'CreateGroup',             component: () => import('@/views/CreateGroupView.vue'),            meta: { requiresAuth: true } },
  { path: '/add-member',             name: 'AddMemberView',           component: () => import('@/views/AddMemberView.vue'),              meta: { requiresAuth: true } },
  { path: '/posts',                  name: 'PostsView',               component: () => import('@/views/PostsView.vue'),                  meta: { requiresAuth: true } },
  { path: '/member-applications',    name: 'MemberApplications',      component: () => import('@/views/MemberApplicationsView.vue'),    meta: { requiresAuth: true } },
  { path: '/post/:postId',           name: 'PostDetail',              component: () => import('@/views/PostDetail.vue'),                 meta: { requiresAuth: true }, props: true },
  { path: '/post/:postID/comments',  name: 'CommentsView',            component: () => import('@/views/CommentsView.vue'),               meta: { requiresAuth: true }, props: true },
  { path: '/explore-post/:postId',   name: 'ExplorePostDetail',       component: () => import('@/views/ExplorePostDetail.vue'),          meta: { requiresAuth: true }, props: route => ({ postId: route.params.postId, replyId: route.params.replyId || null }) },
  { path: '/groups/:id',             name: 'GroupDetail',             component: () => import('@/views/GroupDetailView.vue'),            meta: { requiresAuth: true }, props: true },
  { path: '/member-detail/:id',      name: 'MemberDetail',            component: () => import('@/views/MemberDetailView.vue'),           meta: { requiresAuth: true }, props: true },
  { path: '/member-profile/:id',     name: 'MemberProfile',           component: () => import('@/views/MemberProfileView.vue'),          meta: { requiresAuth: true }, props: true },
  { path: '/member-bio/:id',         name: 'MemberBio',               component: () => import('@/views/MemberBioView.vue'),              meta: { requiresAuth: true }, props: true },

  { path: '*', redirect: '/login' },
]

const router = new VueRouter({
  mode: 'history',
  base: process.env.BASE_URL,
  routes,
})

let isAuthResolved = false
let cachedRole     = null

async function getFirebase() {
  const { auth, db } = await import('@/firebase')
  return { auth, db }
}

function waitForAuth(auth) {
  if (isAuthResolved) return Promise.resolve()
  return new Promise(resolve => {
    const unsub = auth.onAuthStateChanged(() => {
      isAuthResolved = true
      unsub()
      resolve()
    })
  })
}

async function fetchRole(uid) {
  if (cachedRole) return cachedRole
  try {
    const { db }        = await getFirebase()
    const { doc, getDoc } = await import('firebase/firestore')
    const snap          = await getDoc(doc(db, 'user', uid))
    const data          = snap.exists() ? snap.data() : {}
    const raw           = (data.type || data.role || '').toString().trim().toLowerCase()
    cachedRole          = raw || 'member'
    return cachedRole
  } catch {
    return 'member'
  }
}

function safeNext(next, to, target) {
  if (to.path === target) return next()
  return next(target)
}

router.beforeEach(async (to, from, next) => {
  // Always allow Firebase auth action URLs through immediately
  if (to.path === '/__/auth/action') return next()

  const { auth } = await getFirebase()

  if (!router._authListenerRegistered) {
    router._authListenerRegistered = true
    auth.onAuthStateChanged(user => {
      isAuthResolved = true
      if (!user) cachedRole = null
    })
  }

  await waitForAuth(auth)

  const user          = auth.currentUser
  const isPublic      = to.matched.some(r => r.meta.public) || PUBLIC_PATHS.has(to.path)
  const guestOnly     = to.matched.some(r => r.meta.guestOnly)
  const requiresAuth  = to.matched.some(r => r.meta.requiresAuth)
  const requiresAdmin = to.matched.some(r => r.meta.requiresAdmin)
  const expectedType  = to.meta.userType

  if (isPublic) {
    if (guestOnly && user) {
      const role = await fetchRole(user.uid)
      if (role === 'admin') return safeNext(next, to, '/admin')
      return safeNext(next, to, '/waitlist/complete')
    }
    return next()
  }

  if (!user) return safeNext(next, to, '/login')

  const role = await fetchRole(user.uid)

  if (requiresAdmin && role !== 'admin') {
    return safeNext(next, to, '/waitlist/complete')
  }

  if (LAUNCH_CLOSED && role !== 'admin') {
    return safeNext(next, to, '/waitlist/complete')
  }

  if (expectedType && role !== expectedType && role !== 'admin') {
    if (role === 'giver') return safeNext(next, to, '/giver')
    return safeNext(next, to, '/dashboard')
  }

  next()
})

export default router